/**
 * 2FA (TOTP) API client — talks to svdy-authorization (id.svdy.com).
 *
 * Path A Phase 2 (2026-05-26). Endpoints live on svdy-authorization
 * (mounted under /api/v1/me/2fa/*) — unlike most svdy-platform-web API
 * surfaces which target svdy-platform (api.svdy.com), 2FA enrollment
 * + lifecycle live in the auth service alongside the User model.
 *
 * Auth: same access_token used for the rest of the SPA. JWT works
 * cross-service because both svdy-platform and svdy-authorization
 * verify against the same JWKS.
 *
 * Step-up: enroll / disable / regenerate-recovery-codes are gated by
 * `require_recent_auth(max_age_minutes=5)`. The interceptor below
 * mirrors the platform client's step-up handling so the StepUpModal
 * fires on a stale-credential 401.
 */

import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';

import {
  clearTokens,
  getAccessToken,
  refreshSession,
  saveCurrentReturnTo,
} from '../auth/authService';
import { ApiError } from './client';
import { isStepUpCancelled, requestStepUp } from './stepup';

const AUTH_API_URL =
  process.env.REACT_APP_AUTH_URL || 'http://127.0.0.1:8000';

type EnrichedConfig = InternalAxiosRequestConfig & { _retry?: boolean };

const authApi: AxiosInstance = axios.create({
  baseURL: AUTH_API_URL,
  timeout: 15000,
});

authApi.interceptors.request.use((config) => {
  const cfg = config as EnrichedConfig;
  if (!(cfg.headers instanceof AxiosHeaders)) {
    cfg.headers = new AxiosHeaders(cfg.headers);
  }
  const token = getAccessToken();
  if (token) cfg.headers.set('Authorization', `Bearer ${token}`);
  return cfg;
});

authApi.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as EnrichedConfig | undefined;
    if (!original || error.response?.status !== 401 || original._retry) {
      return Promise.reject(toApiError(error));
    }
    const detail = (error.response.data as { detail?: { error?: string } })
      ?.detail;
    if (detail?.error === 'step_up_required') {
      original._retry = true;
      try {
        const newToken = await requestStepUp();
        const headers = AxiosHeaders.from(original.headers);
        headers.set('Authorization', `Bearer ${newToken}`);
        original.headers = headers;
        return authApi(original);
      } catch (stepUpError) {
        // See client.ts: a declined prompt passes through as a cancellation.
        if (isStepUpCancelled(stepUpError)) return Promise.reject(stepUpError);
        return Promise.reject(toApiError(error));
      }
    }
    original._retry = true;
    const refreshResult = await refreshSession();
    if (refreshResult === 'refreshed') {
      const token = getAccessToken();
      const headers = AxiosHeaders.from(original.headers);
      if (token) headers.set('Authorization', `Bearer ${token}`);
      original.headers = headers;
      return authApi(original);
    }

    if (refreshResult === 'unavailable') {
      return Promise.reject(toApiError(error));
    }

    saveCurrentReturnTo();
    clearTokens();
    if (
      typeof window !== 'undefined' &&
      !window.location.pathname.startsWith('/auth/')
    ) {
      window.location.replace('/onboarding');
    }
    return Promise.reject(toApiError(error));
  },
);

function toApiError(error: AxiosError): ApiError {
  const status = error.response?.status ?? 0;
  const data = error.response?.data;
  const url = error.config?.url;
  return new ApiError(status, data ?? error.message, url);
}

// ---------------------------------------------------------------------------
// Types — mirror app/auth/schemas_2fa.py
// ---------------------------------------------------------------------------

export interface TwoFactorStatus {
  enrolled: boolean;
  preparation: boolean;
  enrolled_at: string | null;  // ISO timestamp
  recovery_codes_remaining: number;
}

export interface TwoFactorEnrollResponse {
  secret: string;
  provisioning_uri: string;
  recovery_codes: string[];
}

export interface TwoFactorVerifyEnrollmentResponse {
  enrolled_at: string;
  message: string;
}

export interface TwoFactorRegenerateResponse {
  recovery_codes: string[];
}

// ---------------------------------------------------------------------------
// API surface
// ---------------------------------------------------------------------------

const helper = {
  get: <T>(path: string, config: AxiosRequestConfig = {}) =>
    authApi.get<T>(path, config).then((r) => r.data),
  post: <T>(path: string, body?: unknown, config: AxiosRequestConfig = {}) =>
    authApi.post<T>(path, body, config).then((r) => r.data),
};

export const twoFactorApi = {
  /** Read enrollment state for UI gating. */
  status(): Promise<TwoFactorStatus> {
    return helper.get('/api/v1/me/2fa/status');
  },

  /**
   * Mint fresh TOTP secret + 10 recovery codes. Returns plaintext for
   * the user to save — server only persists encrypted / hashed forms.
   * Step-up gated.
   */
  enroll(): Promise<TwoFactorEnrollResponse> {
    return helper.post('/api/v1/me/2fa/enroll');
  },

  /** Verify the first 6-digit code; transitions Preparation → Enrolled. */
  verifyEnrollment(code: string): Promise<TwoFactorVerifyEnrollmentResponse> {
    return helper.post('/api/v1/me/2fa/verify-enrollment', { code });
  },

  /**
   * Disable 2FA. Accepts current 6-digit TOTP OR a 10-char recovery code
   * (in case the user lost their authenticator). Step-up gated.
   */
  disable(code: string): Promise<{ message: string }> {
    return helper.post('/api/v1/me/2fa/disable', { code });
  },

  /**
   * Replace 10 recovery codes with a fresh batch. Old codes invalidated.
   * Step-up gated. Requires fully-enrolled state.
   */
  regenerateRecoveryCodes(code: string): Promise<TwoFactorRegenerateResponse> {
    return helper.post('/api/v1/me/2fa/regenerate-recovery-codes', { code });
  },
};
